import { useState } from 'react';
import type { ReactNode, Ref } from 'react';
import type { FilterOptionsState, PaperProps } from '@mui/material';
import { Autocomplete, Avatar, Box, Paper, TextField, Typography } from '@mui/material';
import type { Typeahead } from '../hooks/useTypeahead';

declare module '@mui/material/Autocomplete' {
  interface AutocompletePaperSlotPropsOverrides {
    /** Footer rendered under the option list — see {@link UserPickerProps.listHint}. */
    listHint?: ReactNode;
  }
}

/**
 * The dropdown's paper, with an optional footer under the options.
 *
 * Declared at module scope so its identity is stable: a component built inside
 * the render would remount the popup — closing it — every time the hint changed,
 * which is exactly while someone is typing.
 */
function ListPaper({ listHint, children, ...paperProps }: PaperProps & { listHint?: ReactNode }) {
  return (
    <Paper {...paperProps}>
      {children}
      {listHint && (
        <Box
          sx={{
            px: 1.75,
            py: 1,
            fontSize: 11.5,
            color: 'text.secondary',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          {listHint}
        </Box>
      )}
    </Paper>
  );
}

/**
 * Prepends `selected` to `options` when the current page doesn't contain it.
 *
 * Every server-searched picker needs this: the picked user drops out of the
 * fetched page the moment the search term changes, and MUI then both warns
 * ("none of the options match the value") and renders the field as if nothing
 * were chosen. Identity is by `id`, which every user DTO in the app carries.
 */
export function withSelectedOption<T extends { id: string }>(
  options: T[],
  selected: T | null | undefined,
): T[] {
  if (!selected) return options;
  return options.some((o) => o.id === selected.id) ? options : [selected, ...options];
}

export interface UserPickerOption {
  id: string;
  name: string;
  /** Secondary line (e.g. "EMP-10033 · Telecom Admin"). */
  secondary?: string | null;
  /** Right-aligned trailing content on the option row (e.g. a role name). */
  trailing?: ReactNode;
}

/**
 * Client-side filter for the complete-set mode: matches the name **and** the secondary
 * line, so the employee number every row already displays can also be typed to find its
 * owner. MUI's default filter reads `getOptionLabel`, which is the name alone — the number
 * was on screen and was the one thing you could not search by.
 *
 * Terms are matched independently rather than as one substring, so "10033 rosa" finds the
 * same person as "rosa 10033". Someone reading a number off a spreadsheet should not have
 * to guess the order the field expects.
 *
 * Server-searched pickers never reach this — see the `filterOptions` call below.
 */
function filterByNameOrSecondary(
  options: UserPickerOption[],
  { inputValue }: FilterOptionsState<UserPickerOption>,
): UserPickerOption[] {
  const terms = inputValue.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return options;
  return options.filter((option) => {
    const haystack = `${option.name} ${option.secondary ?? ''}`.toLowerCase();
    return terms.every((term) => haystack.includes(term));
  });
}


/**
 * Returns a reference-stable view of `option` for as long as its fields are equal.
 *
 * MUI compares the `value` prop **by reference** to decide whether the value
 * changed (`useAutocomplete.js`: `const valueChange = value !== previousProps.value`),
 * and that comparison is the only thing standing between a blurred field and the
 * effect that rewrites the input box. Hand it a freshly-allocated object each
 * render — which every picker that maps its options per render does — and
 * `valueChange` is permanently true, the `focused` guard never fires, and MUI
 * overwrites what the user is typing with the selected option's label. That is
 * BUG-043's "the search value I type disappears", for the case where the field
 * already holds a value: the default state of every request form, since the
 * subject is seeded to the caller.
 *
 * Adjust-state-during-render (the pattern used elsewhere in this app) rather than
 * a ref, which the lint config forbids reading during render. The freshly-changed
 * object is returned immediately, so there is no one-render lag; from the next
 * render on it is the same reference.
 */
function useStableOption(option: UserPickerOption | null): UserPickerOption | null {
  const [stable, setStable] = useState(option);
  const changed =
    (stable?.id ?? null) !== (option?.id ?? null) ||
    (stable?.name ?? null) !== (option?.name ?? null) ||
    (stable?.secondary ?? null) !== (option?.secondary ?? null);
  if (changed) setStable(option);
  return changed ? option : stable;
}

export interface UserPickerProps {
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  /** Selected user id ('' = none). */
  value: string;
  onChange: (id: string) => void;
  options: UserPickerOption[];
  /** The signed-in user's id; its option is suffixed with {@link youSuffix}. */
  selfId?: string;
  /** Suffix appended to the self option (e.g. " (you)"). */
  youSuffix?: string;
  noOptionsText?: string;
  loadingText?: string;
  helperText?: string;
  /**
   * A note about the *list* — "showing 25 of 3246", say — rendered as a footer
   * inside the dropdown rather than under the field.
   *
   * It belongs with the list because that is what it describes, and because a
   * field-level helper adds a line of text under the input: fine in a form, but in
   * a filter bar it makes one control taller than its neighbours and knocks the row
   * out of alignment (BUG-044). {@link helperText} stays for messages about the
   * *field*, which is validation.
   */
  listHint?: ReactNode;
  /**
   * The caller's {@link Typeahead}. Passing it switches the picker into
   * server-search mode: MUI's client-side filtering goes **off** (`options` is
   * then the server's answer for the current term and must be rendered as-is),
   * the input box becomes controlled by the typeahead, and the dropdown defers
   * to {@link awaiting} instead of showing an older term's rows.
   */
  search?: Typeahead;
  /**
   * True while there is no page for the term in the box — from
   * `typeaheadPage(...).awaiting`. The dropdown renders its loading text rather
   * than the previous term's results, which is the whole of BUG-043.
   */
  awaiting?: boolean;
  /**
   * The resolved option for {@link value} when it is not in {@link options}.
   * With server-side search the picked user drops out of the fetched page as
   * soon as the term changes; without this the field would look cleared.
   */
  selectedOption?: UserPickerOption | null;
  ref?: Ref<HTMLDivElement>;
}

/**
 * "Who is this request for?" type-ahead (the Polaris kit `UserPicker`) — an MUI
 * `Autocomplete` over users, each option showing a name + a secondary id line.
 * Presentational: the caller supplies `options` (and pins self first). Returns
 * the chosen user id.
 *
 * Two modes:
 * - **Client-filtered** (no `onSearchChange`) — the caller passes the complete
 *   candidate set and it is filtered here, over the name and the secondary line
 *   (see {@link filterByNameOrSecondary}). Correct only when the set really is
 *   complete, e.g. `GET /me/submit-candidates`, which is deliberately unpaged.
 * - **Server-searched** (`search` given) — the caller re-queries per settled
 *   term and MUI renders exactly what came back. Required whenever the read is
 *   paged, because filtering one page can never surface a user who isn't on it
 *   (BUG-034). Pass `awaiting` alongside it, or the dropdown will present the
 *   previous term's rows as this term's answer (BUG-043).
 */
export function UserPicker({
  label,
  placeholder,
  required,
  disabled,
  loading,
  value,
  onChange,
  options,
  selfId,
  youSuffix,
  noOptionsText,
  loadingText,
  helperText,
  listHint,
  search,
  awaiting = false,
  selectedOption,
  ref,
}: UserPickerProps) {
  const serverSearched = !!search;
  const match =
    options.find((o) => o.id === value) ??
    (selectedOption && selectedOption.id === value ? selectedOption : null);
  // `selected` is what MUI receives as `value`, so its identity has to be stable —
  // see useStableOption. Matching in the list stays by id (isOptionEqualToValue).
  const selected = useStableOption(match);
  // Keep the selection in the option list even when the current page doesn't
  // contain it — otherwise MUI warns that the value matches no option.
  const renderedOptions =
    selected && !options.some((o) => o.id === selected.id) ? [selected, ...options] : options;

  const withYou = (o: UserPickerOption) =>
    selfId && o.id === selfId && youSuffix ? `${o.name}${youSuffix}` : o.name;

  return (
    <Autocomplete<UserPickerOption>
      ref={ref}
      options={renderedOptions}
      value={selected}
      loading={loading}
      loadingText={loadingText}
      disabled={disabled}
      noOptionsText={noOptionsText}
      getOptionLabel={(o) => withYou(o)}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      // The server already filtered for this term; re-filtering here would hide
      // matches whose fetched fields don't contain the raw substring (an email
      // or employee-number hit, say). In the complete-set mode there is no server
      // to defer to, so the filter runs here — over the number as well as the name.
      // While `awaiting`, render nothing rather than the page fetched for an
      // older term — MUI then shows `loadingText`. The selection stays in
      // `options` (not in the filtered result) so MUI can still match `value`.
      filterOptions={
        serverSearched ? (opts) => (awaiting ? [] : opts) : filterByNameOrSecondary
      }
      slots={{ paper: ListPaper }}
      slotProps={{ paper: { listHint } }}
      onChange={(_e, next) => onChange(next?.id ?? '')}
      {...(search
        ? { inputValue: search.inputValue, onInputChange: search.onInputChange }
        : {})}
      renderOption={(props, option) => {
        const { key, ...rest } = props as typeof props & { key: string };
        return (
          <Box component="li" key={key} {...rest} sx={{ gap: 1.25 }}>
            <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'primary.main' }}>
              {option.name.charAt(0)}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.primary' }} noWrap>
                {withYou(option)}
              </Typography>
              {option.secondary && (
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }} noWrap>
                  {option.secondary}
                </Typography>
              )}
            </Box>
            {option.trailing && (
              <Typography sx={{ fontSize: 11, color: 'text.secondary', flexShrink: 0 }}>
                {option.trailing}
              </Typography>
            )}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label={label}
          placeholder={placeholder}
          required={required}
          helperText={helperText}
        />
      )}
    />
  );
}
